import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Eye, Download, Inbox } from 'lucide-react'
import toast from 'react-hot-toast'
import referralService from '@/services/referralService'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import Pagination from '@/components/common/Pagination'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import DataTable from '@/components/common/DataTable'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'

const statusVariants = {
  clicked: 'secondary',
  registered: 'default',
  trial_started: 'warning',
  trial_converted: 'success',
  active: 'success',
  churned: 'danger',
}

const statusLabels = {
  clicked: 'Click',
  registered: 'Registrado',
  trial_started: 'En Trial',
  trial_converted: 'Convertido',
  active: 'Activo',
  churned: 'Cancelado',
}

export default function Referrals() {
  const navigate = useNavigate()
  const [referrals, setReferrals] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  useEffect(() => {
    fetchReferrals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter])

  const fetchReferrals = async () => {
    setLoading(true)
    try {
      const filters = {}
      if (statusFilter) filters.status = statusFilter
      if (search) filters.search = search
      const data = await referralService.getReferrals(filters, {
        page: page + 1,
        limit: rowsPerPage,
      })
      setReferrals(data.referrals || [])
      setTotal(data.pagination?.total || 0)
    } catch {
      toast.error('Error al cargar referidos')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPage(0)
      fetchReferrals()
    }
  }

  const handleExport = async () => {
    try {
      const filters = {}
      if (statusFilter) filters.status = statusFilter
      if (search) filters.search = search
      const blob = await referralService.exportReferrals(filters)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `referidos-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success('Exportación completada')
    } catch {
      toast.error('Error al exportar')
    }
  }

  const renderEmpty = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Inbox size={36} className="text-neutral-400 dark:text-neutral-500 mb-2" />
      <p className="text-body text-foreground font-medium">No hay referidos</p>
      <p className="text-caption text-subtle mt-1">
        Cuando alguien use tu link, aparecerá aquí.
      </p>
    </div>
  )

  const columnas = [
    {
      clave: 'usuario', titulo: 'Usuario', prioridad: 'titulo',
      celda: (r) => (
        <div className="min-w-0">
          <p className="font-medium text-foreground truncate">
            {r.referredUser?.firstName} {r.referredUser?.lastName}
          </p>
          <p className="text-caption text-subtle truncate">{r.referredUser?.email}</p>
        </div>
      ),
    },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (r) => (
        <Badge variant={statusVariants[r.status] || 'secondary'}>
          {statusLabels[r.status] || r.status}
        </Badge>
      ),
    },
    { clave: 'plan', titulo: 'Plan', prioridad: 'meta', celda: (r) => <span className="text-foreground">{r.subscription?.plan || '—'}</span> },
    {
      clave: 'registro', titulo: 'Fecha registro', prioridad: 'meta',
      celda: (r) => (
        <span className="text-foreground">
          {r.referredUser?.registeredAt ? new Date(r.referredUser.registeredAt).toLocaleDateString('es-ES') : '—'}
        </span>
      ),
    },
    {
      clave: 'comision', titulo: 'Comisión', prioridad: 'meta',
      celda: (r) => (
        <Badge variant={r.isCommissionActive ? 'success' : 'secondary'}>
          {r.isCommissionActive ? 'Sí' : 'No'}
        </Badge>
      ),
    },
    {
      clave: 'acciones', titulo: 'Acciones', alineacion: 'derecha', prioridad: 'oculta',
      celda: (r) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/referrals/${r._id}`)
          }}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-subtle hover:bg-surface-muted hover:text-primary"
          aria-label="Ver detalle"
        >
          <Eye size={16} />
        </button>
      ),
    },
  ]

  return (
    <PageContainer>
      <PageHeader
        title="Referidos"
        description="Gestiona y monitorea tus referidos"
        actions={
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download size={14} /> <span className="hidden sm:inline">Exportar CSV</span>
          </Button>
        }
      />

      {/* Filters */}
      <Card className="p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 pointer-events-none"
            />
            <Input
              placeholder="Buscar por email o nombre…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearch}
              className="pl-10"
            />
          </div>
          <Select
            value={statusFilter || 'all'}
            onValueChange={(v) => {
              setStatusFilter(v === 'all' ? '' : v)
              setPage(0)
            }}
          >
            <SelectTrigger className="sm:w-48">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(statusLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Una sola tabla: tarjetas en el teléfono, tabla desde md */}
      <DataTable
        tarjetasEnMovil
        etiquetaTabla="Referidos"
        className="bg-card"
        columnas={columnas}
        filas={referrals}
        claveDeFila={(r) => r._id}
        cargando={loading}
        onFilaClick={(r) => navigate(`/referrals/${r._id}`)}
        vacio={<Card>{renderEmpty()}</Card>}
      />
      {!loading && referrals.length > 0 && (
        <Card className="!py-0">
          <Pagination
            page={page}
            pageSize={rowsPerPage}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setRowsPerPage(s)
              setPage(0)
            }}
          />
        </Card>
      )}
    </PageContainer>
  )
}
